// Catalog.ID Client — Device unlock factors (DOM-free)
// ============================================================================
// device-setup-restructure §6.1. Every decision the unlock screen makes lives
// here: which inputs to paint, what a refusal reads like to a member, what
// survives a retry, and when the flow gives up on factors and asks for the
// supersecret instead. The screen (auth/login-view.ts) owns the DOM and
// nothing else — the project's pattern since `authenticator.ts`.
//
// Two rules of the wire shape drive the whole module (§3, M1 as built):
//
//  * **The first attempt of a session carries nothing.** A login through the
//    Authenticator gate leaves a single-use in-memory PASS on the session, and
//    the node prefers that pass over any code the client supplies — only a
//    code-less unlock spends it. Sending a code on the first try would waste
//    the pass and leave the member waiting up to thirty seconds for the next
//    code. So: bare unlock, then render exactly what the refusal names.
//  * **`factors_required` lists only what is STILL missing for THIS call.** On
//    a gated session's first unlock of a *PIN + Authenticator* device the
//    answer is `["pin"]`, not `["pin","totp"]`.
//
// The `{cid}_unlock_factors` hint exists only so the first paint is not blank;
// the node's answer overrides it every time (`resolveUnlockFactors`).

import * as deviceApi from '../api/device';
import type { DeviceUnlockRequest, DeviceUnlockResponse } from '../api/types';
import { ApiError, isSessionRejected } from '../api/client';
import { describeAuthenticatorError } from './authenticator';
import {
    clearUnlockFactors, getUnlockFactors, setUnlockFactors, StaleDeviceKeysError,
    type UnlockFactorHint,
} from '../state/device-storage';

export type { UnlockFactorHint };

/** One thing the member types. Mirrors the node's `factors` vocabulary. */
export type UnlockFactor = 'pin' | 'totp';

const KNOWN_FACTORS: readonly string[] = ['pin', 'totp'];

/** The node's two verbatim refusal strings — the only two that need telling
 *  apart, since both arrive as a 401 on `unlock`. */
const WRONG_PIN = 'Wrong PIN';
const CODE_FAILURE = 'Invalid authenticator code';

function serverError(err: ApiError): string | null {
    const body = err.body as { error?: unknown } | null;
    return typeof body?.error === 'string' ? body.error : null;
}

// ── what the node is still asking for ───────────────────────────────────────

/**
 * The factors named by a `400 { error: "factors_required", factors: [...] }`
 * refusal, in the node's order (PIN first — it is verified first, §1 item 3).
 * Null for every other error.
 *
 * Strict on purpose: a list carrying a factor this client cannot render would,
 * if partially honoured, loop the member forever on a screen that can never
 * satisfy the node. Reading it as "not a factors refusal" drops through to the
 * supersecret path, which always can.
 */
export function requiredFactorsFrom(err: unknown): UnlockFactor[] | null {
    if (!(err instanceof ApiError) || err.status !== 400) return null;
    const body = err.body as { error?: unknown; factors?: unknown } | null;
    if (!body || body.error !== 'factors_required' || !Array.isArray(body.factors)) return null;
    if (body.factors.length === 0) return null;
    if (!body.factors.every(f => typeof f === 'string' && KNOWN_FACTORS.includes(f))) return null;
    return body.factors as UnlockFactor[];
}

// ── one line per refusal ────────────────────────────────────────────────────

/**
 * What the member reads. One line per case, and the code line is delegated to
 * {@link describeAuthenticatorError} rather than copied: the node deliberately
 * never says whether a code was wrong, replayed or out of window, and two
 * screens must not be able to drift into saying different things about it.
 */
export function describeUnlockError(err: unknown): string {
    if (err instanceof StaleDeviceKeysError) {
        return 'Your device keys are out of date — unlock with your supersecret to refresh them';
    }
    if (isSessionRejected(err)) {
        return 'Your session has ended — sign in with your password again.';
    }
    if (err instanceof ApiError) {
        if (err.status === 423) return 'This device is locked. Unlock it with your supersecret.';
        if (err.status === 429) return describeAuthenticatorError(err);
        if (err.status === 404) return 'This device is no longer registered — unlock with your supersecret.';
        const message = serverError(err);
        if (message === WRONG_PIN) return 'Wrong PIN. Try again.';
        if (message === CODE_FAILURE) return describeAuthenticatorError(err);
    }
    return err instanceof Error && err.message ? err.message : 'Device unlock failed';
}

// ── the round trip ──────────────────────────────────────────────────────────

/** What one unlock attempt carries. `factors` is what the NODE asked for; a
 *  value not named there is held back even when the screen still holds it. */
export interface UnlockAttempt {
    deviceToken: string;
    /** Omit for the first attempt of a session — that is what spends a gate
     *  pass (§5.3, M1 deviation 5). */
    factors?: readonly UnlockFactor[];
    pin?: string;
    code?: string;
}

export type UnlockOutcome =
    | { status: 'unlocked'; deviceEncryptionKey: string }
    | { status: 'factors-required'; factors: UnlockFactor[] }
    | { status: 'wrong-pin'; message: string }
    | { status: 'wrong-code'; message: string }
    | { status: 'rate-limited'; message: string; retryAfterSeconds: number | null }
    | { status: 'locked'; message: string }
    | { status: 'device-unknown'; message: string }
    | { status: 'stale-keys'; message: string }
    | { status: 'session-rejected'; message: string }
    | { status: 'failed'; message: string };

export interface UnlockDeps {
    unlock(req: DeviceUnlockRequest): Promise<DeviceUnlockResponse>;
}

export const defaultUnlockDeps: UnlockDeps = {
    unlock: (req) => deviceApi.unlockDevice(req),
};

/**
 * One unlock attempt, carrying only the factors the node asked for, with every
 * failure classified. Never throws: the screen's job is to render the outcome,
 * not to re-derive it from an exception.
 */
export async function unlockWithFactors(
    attempt: UnlockAttempt, deps: UnlockDeps = defaultUnlockDeps,
): Promise<UnlockOutcome> {
    const wanted = attempt.factors;
    const req: DeviceUnlockRequest = { deviceToken: attempt.deviceToken };
    if (attempt.pin !== undefined && (!wanted || wanted.includes('pin'))) req.pin = attempt.pin;
    if (attempt.code !== undefined && (!wanted || wanted.includes('totp'))) req.code = attempt.code;
    try {
        const resp = await deps.unlock(req);
        return { status: 'unlocked', deviceEncryptionKey: resp.deviceEncryptionKey };
    } catch (err) {
        return classifyUnlockFailure(err);
    }
}

/**
 * Classify anything the unlock path can throw — including the
 * {@link StaleDeviceKeysError} that comes later, from decrypting this device's
 * copy, so the screen has ONE outcome vocabulary for the whole operation.
 */
export function classifyUnlockFailure(err: unknown): UnlockOutcome {
    const factors = requiredFactorsFrom(err);
    if (factors) return { status: 'factors-required', factors };

    const message = describeUnlockError(err);
    if (err instanceof StaleDeviceKeysError) return { status: 'stale-keys', message };
    if (isSessionRejected(err)) return { status: 'session-rejected', message };
    if (err instanceof ApiError) {
        if (err.status === 423) return { status: 'locked', message };
        if (err.status === 404) return { status: 'device-unknown', message };
        if (err.status === 429) {
            const body = err.body as { retryAfterSeconds?: unknown } | null;
            const seconds = typeof body?.retryAfterSeconds === 'number' ? body.retryAfterSeconds : null;
            return { status: 'rate-limited', message, retryAfterSeconds: seconds };
        }
        const text = serverError(err);
        if (text === WRONG_PIN) return { status: 'wrong-pin', message };
        if (text === CODE_FAILURE) return { status: 'wrong-code', message };
    }
    return { status: 'failed', message };
}

/**
 * What the screen keeps for the next attempt.
 *
 * The node verifies the PIN FIRST and outside the code's limiter (§1 item 3),
 * so the two refusals mean opposite things about the other field: a code
 * refusal proves the PIN was right (keep it — re-typing an 8-digit PIN for a
 * mistyped code is a lie about which factor failed), and a PIN refusal means
 * the code was never reached, so it is neither wrong nor spent.
 */
export function nextAttemptAfter(
    outcome: UnlockOutcome, attempt: { pin?: string; code?: string },
): { pin?: string; code?: string } {
    if (outcome.status === 'wrong-code') return { pin: attempt.pin, code: undefined };
    if (outcome.status === 'wrong-pin') return { pin: undefined, code: attempt.code };
    return { pin: attempt.pin, code: attempt.code };
}

/** True when no amount of re-typing helps and the member needs the supersecret
 *  (whose device-setup step rewrites the whole local copy). */
export function routesToSupersecret(outcome: UnlockOutcome): boolean {
    return outcome.status === 'locked'
        || outcome.status === 'device-unknown'
        || outcome.status === 'stale-keys'
        || outcome.status === 'failed';
}

// ── the local hint ──────────────────────────────────────────────────────────

/** The hint for a device row, or null for *No PIN* — which asks for nothing. */
export function hintForMode(accessMode: string, totpRequired: boolean): UnlockFactorHint | null {
    if (accessMode === 'pin') return totpRequired ? 'pin+totp' : 'pin';
    return totpRequired ? 'totp' : null;
}

/** The factors a hint stands for, in the node's order. */
export function factorsFromHint(hint: UnlockFactorHint | null): UnlockFactor[] {
    if (hint === 'pin') return ['pin'];
    if (hint === 'totp') return ['totp'];
    if (hint === 'pin+totp') return ['pin', 'totp'];
    return [];
}

/** This device's remembered factors. First paint only. */
export function readUnlockFactorHint(cid: string): UnlockFactorHint | null {
    return getUnlockFactors(cid);
}

/** Record the factors a device row now asks for — after setup, a mode change
 *  or a reset. Called with the mode that was actually WRITTEN, never with what
 *  was merely offered. */
export function rememberUnlockFactors(cid: string, accessMode: string, totpRequired: boolean): void {
    const hint = hintForMode(accessMode, totpRequired);
    if (hint) setUnlockFactors(cid, hint);
    else clearUnlockFactors(cid);
}

/** What to paint before the node has said anything. */
export function initialUnlockFactors(cid: string): UnlockFactor[] {
    return factorsFromHint(readUnlockFactorHint(cid));
}

/**
 * The node's answer wins. The hint can be stale in both directions — the mode
 * may have been changed from another browser, and a gated session owes only
 * the PIN on its first unlock — so it is used ONLY while `fromServer` is null.
 */
export function resolveUnlockFactors(
    hint: UnlockFactorHint | null, fromServer: readonly UnlockFactor[] | null,
): UnlockFactor[] {
    return fromServer ? [...fromServer] : factorsFromHint(hint);
}
